/**
 * Web Speech API helpers (BW18 PR-J).
 *
 * Browser TTS quality varies — Safari/iOS deliver the cleanest output,
 * Chrome on macOS uses system voices, Firefox falls back to platform
 * voices. The API is good enough for vocab review; ElevenLabs / Google
 * TTS upgrade hooks are spec'd but not wired.
 */

const LANG_MAP: Record<string, string> = {
  fr: "fr-FR",
  en: "en-US",
  de: "de-DE",
  es: "es-ES",
  it: "it-IT",
  pt: "pt-PT",
  nl: "nl-NL",
};

export function bcp47(code: string): string {
  return LANG_MAP[code] ?? code;
}

export function isTtsAvailable(): boolean {
  return typeof window !== "undefined" && "speechSynthesis" in window;
}

export function speak(text: string, lang: string, opts?: { rate?: number }): void {
  if (!isTtsAvailable()) return;
  window.speechSynthesis.cancel();
  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = bcp47(lang);
  utterance.rate = opts?.rate ?? 0.9;
  window.speechSynthesis.speak(utterance);
}

type SpeechRecognitionEventLike = {
  results: { 0: { 0: { transcript: string } } };
};

type SpeechRecognitionLike = {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((e: SpeechRecognitionEventLike) => void) | null;
  onerror: ((e: { error?: string }) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
};

export function isAsrAvailable(): boolean {
  if (typeof window === "undefined") return false;
  const w = window as unknown as Record<string, unknown>;
  return Boolean(w.webkitSpeechRecognition ?? w.SpeechRecognition);
}

export function listenOnce(
  lang: string,
  onResult: (text: string) => void,
  onError?: (err: string) => void,
): (() => void) | null {
  if (!isAsrAvailable()) {
    onError?.("Speech recognition not available in this browser");
    return null;
  }
  const w = window as unknown as Record<string, unknown>;
  const Ctor = (w.webkitSpeechRecognition ?? w.SpeechRecognition) as new () => SpeechRecognitionLike;
  const recognition = new Ctor();
  recognition.lang = bcp47(lang);
  recognition.continuous = false;
  recognition.interimResults = false;
  recognition.onresult = (e) => {
    const said = e.results[0][0].transcript;
    onResult(said);
  };
  recognition.onerror = (e) => onError?.(e.error ?? "speech-error");
  recognition.start();
  return () => recognition.stop();
}
